import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { walletRows, transactionLabel, formatAmount, formatTimestamp } from '../utils/saldo'

// The theme's success.main is Paper Dashboard's light #6bd098 (about 2:1 on white) - too
// pale for text. This darker green reads at about 5:1.
const POSITIVE_COLOR = '#1e7b45'

const amountColor = (n) => (n < 0 ? 'error.main' : n > 0 ? POSITIVE_COLOR : 'text.secondary')

// A person's wallets (Saldot tab): one accordion per venue + category, its transactions
// opening underneath it. kind: 'saldo' (users-getsaldotransactions) or 'storage'
// (users-getstoragetransactions) - same payload shape, different titles and units.
function WalletList({ entries, kind, emptyText }) {
  const rows = walletRows(entries, kind)

  if (rows.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
        {emptyText}
      </Typography>
    )
  }

  return (
    <Box sx={{ mt: 1 }}>
      {rows.map((wallet) => {
        const showValidUntil = wallet.transactions.some((t) => t.validuntil)
        return (
          <Accordion key={wallet.key} defaultExpanded={rows.length === 1} disableGutters variant='outlined'>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 2, pr: 1 }}>
                <Typography sx={{ flex: 1 }}>
                  {wallet.venueLabel ? wallet.venueLabel + ' – ' : ''}
                  {wallet.title}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: amountColor(wallet.total) }}>
                  {formatAmount(wallet.total, wallet.unit)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {wallet.transactions.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Ei tapahtumia
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Päivä</TableCell>
                      <TableCell>Tapahtuma</TableCell>
                      <TableCell>Kuvaus</TableCell>
                      {showValidUntil ? <TableCell sx={{ whiteSpace: 'nowrap' }}>Voimassa</TableCell> : null}
                      <TableCell align='right'>Summa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wallet.transactions.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(t.timestamp)}</TableCell>
                        <TableCell>{transactionLabel(t, kind)}</TableCell>
                        <TableCell>
                          {t.description}
                          {t.category ? (
                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                              {t.category}
                            </Typography>
                          ) : null}
                        </TableCell>
                        {showValidUntil ? <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.validuntil || ''}</TableCell> : null}
                        <TableCell align='right' sx={{ whiteSpace: 'nowrap', color: amountColor(Number(t.amount) || 0) }}>
                          {formatAmount(t.amount, wallet.unit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

export default WalletList
